import React, { useState, useEffect } from "react";

export default function ProfilePanel({
  profile,
  onUpdateBio,
  formatCurrency,
  formatNumber,
  regionMap = {},
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState("");

  useEffect(() => {
    setBioDraft(profile?.userBio || "");
  }, [profile?.userBio]);

  if (!profile) return null;

  const tierColors = {
    Basic: "#FFFFFF",
    Preferred: "#3B82F6",
    Premium: "#FBBF24",
    Power: "#A855F7",
    Moderator: "#50E3C2"
  };
  const accentColor = tierColors[profile.userLevel] || tierColors.Basic;

  const safeNum = (val) => formatNumber(Number(val) || 0);
  const safeCurr = (val) => formatCurrency(Number(val) || 0);

  const getDaysInCompany = (timestamp) => {
    if (!timestamp || isNaN(timestamp)) return "—";
    const joinDate = new Date(Number(timestamp));
    const currentDate = new Date();
    const diffTime = Math.max(0, currentDate - joinDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return `${formatNumber(diffDays)} DAYS`;
  };

  const totalCostBasis = 
    (Number(profile.userSharesPurchasedUsd) || 0) +
    (Number(profile.userCoinsPurchasedUsd) || 0) +
    (Number(profile.userForexPurchasedUsd) || 0) +
    (Number(profile.userBondsPurchasedUsd) || 0) +
    (Number(profile.userFuturesPurchasedUsd) || 0) +
    (Number(profile.userOptionsPurchasedUsd) || 0) +
    (Number(profile.userGoldPurchasedUsd) || 0) +
    (Number(profile.userAssetsPurchasedUsd) || 0);

  const marketProfit = Number(profile.userMarketGains) || 0;
  const costToProfit = totalCostBasis > 0 ? `1 : ${(marketProfit / totalCostBasis).toFixed(2)}` : "N/A";

  const liquidCapital = Number(profile.userMoney) || 0;
  const portfolioValue = Number(profile.userPortfolioValue) || 0;
  const realHoldings = Math.max(0, portfolioValue - liquidCapital);

  const handleSave = () => {
    if (onUpdateBio) onUpdateBio(bioDraft);
    setIsEditing(false);
  };

  const sections = [
    {
      title: "IDENTITY",
      rows: [
        ["Name", profile.userDisplayName || "—"],
        ["Title", profile.userTitle || "—"],
        ["Rank", profile.userRank || "—"],
        ["Region", regionMap?.[profile.userRegion] || profile.userRegion || "Unknown"],
        ["Company", profile.userCompany || "—"],
        ["Company Ticker", profile.userCompanyTicker || "—"],
        ["Days In Company", getDaysInCompany(profile.userCompanyJoinDate)],
        ["Achievements Unlocked", `${safeNum(profile.userAchievements)} / 46`],
      ],
    },
    {
      title: "FINANCE",
      rows: [
        ["Net Market Gains", safeCurr(marketProfit)],
        ["Real Holdings", safeCurr(realHoldings)],
        ["Calculated Cost Basis", safeCurr(totalCostBasis)],
        ["Cost-to-Profit Ratio", costToProfit],
        ["VP Earned", safeNum(profile.userVpEarned)],
        ["VP Spent", safeNum(profile.userVpSpent)],
        ["Total USD Spent", safeCurr(profile.userUsdSpent)],
        ["Taxes Paid", safeCurr(profile.userTaxesPaid)],
        ["Total Exchanged", safeNum(profile.userSharesExchanged)],
        ["Total Transferred", safeNum(profile.userSharesTransferred)],
      ],
    },
    {
      title: "STOCKS",
      rows: [
        ["Shares Purchased", safeNum(profile.userSharesPurchased)],
        ["Shares Sold", safeNum(profile.userSharesSold)],
        ["Shares Purchased Value", safeCurr(profile.userSharesPurchasedUsd)],
        ["Shares Sold Value", safeCurr(profile.userSharesSoldUsd)],
      ],
    },
    {
      title: "CRYPTO",
      rows: [
        ["Coins Purchased", safeNum(profile.userCoinsPurchased)],
        ["Coins Sold", safeNum(profile.userCoinsSold)],
        ["Coins Purchased Value", safeCurr(profile.userCoinsPurchasedUsd)],
        ["Coins Sold Value", safeCurr(profile.userCoinsSoldUsd)],
      ],
    },
    {
      title: "FOREX",
      rows: [
        ["Forex Purchased", safeNum(profile.userForexPurchased)],
        ["Forex Sold", safeNum(profile.userForexSold)],
        ["Forex Purchased Value", safeCurr(profile.userForexPurchasedUsd)],
        ["Forex Sold Value", safeCurr(profile.userForexSoldUsd)],
      ],
    },
    {
      title: "BONDS & DERIVATIVES",
      rows: [
        ["Bonds Purchased", safeNum(profile.userBondsPurchased)],
        ["Bonds Purchased Value", safeCurr(profile.userBondsPurchasedUsd)],
        ["Bond Profit", safeCurr(profile.userBondsProfit)],
        ["Contracts Written", safeNum(profile.userContractsWritten)],
        ["Contracts Accepted", safeNum(profile.userContractsAccepted)],
        ["Options Purchased", safeNum(profile.userOptionsPurchased)],
        ["Options Purchased Value", safeCurr(profile.userOptionsPurchasedUsd)],
        ["Options Sold", safeNum(profile.userOptionsSold)],
        ["Options Sold Value", safeCurr(profile.userOptionsSoldUsd)],
        ["Futures Purchased", safeNum(profile.userFuturesPurchased)],
        ["Futures Purchased Value", safeCurr(profile.userFuturesPurchasedUsd)],
        ["Futures Profit", safeCurr(profile.userFuturesProfit)],
      ],
    },
    {
      title: "CREDIT",
      rows: [
        ["Funds Loaned", safeCurr(profile.userMoneyLoaned)],
        ["Funds Staked", safeCurr(profile.userMoneyStaked)],
      ],
    },
    {
      title: "ASSETS",
      rows: [
        ["Assets Purchased", safeNum(profile.userAssetsPurchased)],
        ["Assets Purchased Value", safeCurr(profile.userAssetsPurchasedUsd)],
        ["Assets Sold", safeNum(profile.userAssetsSold)],
        ["Assets Sold Value", safeCurr(profile.userAssetsSoldUsd)],
        ["Highest Price Paid", `${profile.userMostPaidAsset || "—"} (${safeCurr(profile.userMostPaidAssetUsd)})`],
        ["Highest Price Sold", `${profile.userMostSoldAsset || "—"} (${safeCurr(profile.userMostSoldAssetUsd)})`],
      ],
    },
    {
      title: "GOLD",
      rows: [
        ["Gold Minted", safeNum(profile.userGoldMinted)],
        ["Gold Earned", safeNum(profile.userGoldEarned)],
        ["Gold Won", safeNum(profile.userGoldWon)],
        ["Gold Purchased", safeNum(profile.userGoldPurchased)],
        ["Gold Purchased Value", safeCurr(profile.userGoldPurchasedUsd)],
        ["Gold Sold", safeNum(profile.userGoldSold)],
        ["Gold Sold Value", safeCurr(profile.userGoldSoldUsd)],
        ["Gold Spent", safeNum(profile.userGoldSpent)],
        ["Highest Price Paid", safeCurr(profile.userMostPaidGold)],
        ["Lowest Price Paid", safeCurr(profile.userLeastPaidGold)],
        ["Highest Price Sold", safeCurr(profile.userMostSoldGold)],
      ],
    },
    {
      title: "INCOME",
      rows: [
        ["Work Earnings", safeCurr(profile.userWorkGains)],
        ["Contract Profits", safeCurr(profile.userContractsGains)],
        ["Exchange Profits", safeCurr(profile.userExchangeGains)],
        ["Tips Received", safeCurr(profile.userTipsReceived)],
        ["Tips Given", safeCurr(profile.userTipsGiven)],
        ["Interest Capitalized", safeCurr(profile.userInterestReceived)],
        ["Dividends Received", safeCurr(profile.userDividendsReceived)],
        ["Donations", safeCurr(profile.userDonations)],
        ["Referrals", safeNum(profile.userReferrals)],
        ["Referral Earnings", safeCurr(profile.userLifetimeReferrals)],
        ["Jackpots Earned", safeCurr(profile.userLifetimeJackpots)],
        ["Discord Winnings", safeCurr(profile.userWinnings)],
        ["Assets Claimed", safeNum(profile.userDailiesClaimed)],
      ],
    },
    {
      title: "SIGNAL",
      rows: [
        ["Signals Intercepted", safeNum(profile.userSignals)],
        ["Total Exp", safeNum(profile.userSignalXP)],
        ["Highest Score", `${safeNum(profile.userSignalBestCombo)}`],
        ["Average Score", `${safeNum(profile.userSignalAvgCombo)}`],
      ],
    },
  ];

  return (
    <div 
      style={{ 
        fontFamily: "'JetBrains Mono', monospace", 
        padding: "12px 4px", 
        minHeight: "100%",
        boxSizing: "border-box"
      }}
    >
      {/* --- BOX 1: EDITABLE SUMMARY BANNER WITH USERLEVEL EDGE COLOR --- */}
      <div 
        style={{
          background: "rgba(20, 20, 20, 0.7)",
          border: "1px solid #333",
          borderLeft: `4px solid ${accentColor}`,
          borderRadius: "0px 4px 4px 0px",
          padding: "14px 16px",
          marginBottom: "16px"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #222", paddingBottom: "6px", marginBottom: "10px" }}>
          <span style={{ color: "#888", fontSize: "0.62rem", fontWeight: "bold", letterSpacing: "1.5px" }}>SUMMARY</span>
          <div style={{ display: "flex", gap: "8px" }}>
            {isEditing && (
              <button
                onClick={() => {
                  setBioDraft(profile?.userBio || "");
                  setIsEditing(false);
                }}
                style={{
                  background: "transparent",
                  border: "1px solid #ff4d4d",
                  color: "#ff4d4d",
                  fontSize: "0.55rem",
                  cursor: "pointer",
                  padding: "2px 8px",
                  borderRadius: "2px",
                  fontWeight: "bold"
                }}
              >
                CANCEL
              </button>
            )}
            <button
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              style={{
                background: "transparent",
                border: `1px solid ${isEditing ? "#50E3C2" : "#888"}`,
                color: isEditing ? "#50E3C2" : "#888",
                fontSize: "0.55rem",
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: "2px",
                fontWeight: "bold"
              }}
            >
              {isEditing ? "CONFIRM" : "UPDATE"}
            </button>
          </div>
        </div>

        {isEditing ? (
          <textarea
            value={bioDraft}
            onChange={(e) => setBioDraft(e.target.value)}
            style={{
              width: "100%",
              background: "rgba(15, 15, 15, 0.9)",
              color: "#F8F9FA",
              border: "1px solid #50E3C2",
              fontSize: "0.7rem",
              padding: "10px",
              minHeight: "75px",
              fontFamily: "inherit",
              outline: "none",
              resize: "none",
              boxSizing: "border-box",
              lineHeight: "1.4"
            }}
          />
        ) : (
          <div style={{ color: "#F8F9FA", fontSize: "0.7rem", lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
            {profile.userBio || "No summary profile description recorded."}
          </div>
        )}
      </div>

      {/* --- DASHBOARD WRAPPER RESPONSIVE GRID MATRIX --- */}
      <div 
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
          gap: "12px",
          width: "100%"
        }}
      >
        {sections.map((section) => (
          <div 
            key={section.title}
            style={{
              background: "rgba(20, 20, 20, 0.45)",
              border: "1px solid rgba(80, 227, 194, 0.12)",
              borderRadius: "4px",
              padding: "12px",
              boxSizing: "border-box"
            }}
          >
            {/* Header Box Strip */}
            <div 
              style={{ 
                color: "#888", 
                fontSize: "0.6rem", 
                fontWeight: "bold", 
                letterSpacing: "1.2px", 
                borderBottom: "1px solid rgba(80, 227, 194, 0.2)",
                paddingBottom: "5px",
                marginBottom: "8px"
              }}
            >
              {section.title}
            </div>

            {/* Structured Box Records */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {section.rows.map(([label, value], idx) => (
                  <tr 
                    key={label}
                    style={{
                      background: idx % 2 === 0 ? "rgba(80, 227, 194, 0.015)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "5px 6px", borderBottom: "1px solid rgba(255, 255, 255, 0.02)" }}>
                      <span style={{ color: "#50E3C2", fontSize: "0.65rem", fontWeight: "bold" }}>
                        {label.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "5px 6px", color: "#F8F9FA", fontSize: "0.68rem", textAlign: "right", fontVariantNumeric: "tabular-nums", borderBottom: "1px solid rgba(255, 255, 255, 0.02)" }}>
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}